import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  IconButton,
} from '@chakra-ui/react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { TicketService } from '@/lib/services/ticket'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import type { TicketPart } from '@/types/parentTicket'

type Props = {
  ticketId: string
  isReadOnly?: boolean
  onPartsChange?: () => void
}

const WHSE_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'not_available', label: 'Not Available' },
] as const

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleDateString(undefined, { dateStyle: 'short' })
  } catch {
    return String(iso)
  }
}

export default function PartsInfoSection({ ticketId, isReadOnly = false, onPartsChange }: Props) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const [parts, setParts] = useState<TicketPart[]>([])
  const [loading, setLoading] = useState(true)
  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure()
  const [editingPart, setEditingPart] = useState<TicketPart | null>(null)
  const [formPartName, setFormPartName] = useState('')
  const [formPartCode, setFormPartCode] = useState('')
  const [formMrCreatedAt, setFormMrCreatedAt] = useState('')
  const [formExpectedDays, setFormExpectedDays] = useState<string>('')
  const [formWhse, setFormWhse] = useState<'available' | 'not_available' | ''>('')
  const [formFeLeadDays, setFormFeLeadDays] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)

  const fetchParts = useCallback(async () => {
    if (!ticketId) return
    setLoading(true)
    const res = await TicketService.getTicketParts(ticketId)
    setLoading(false)
    if (res.success && res.data) setParts(res.data)
    else setParts([])
  }, [ticketId])

  useEffect(() => {
    fetchParts()
  }, [fetchParts])

  const openAdd = () => {
    setEditingPart(null)
    setFormPartName('')
    setFormPartCode('')
    setFormMrCreatedAt('')
    setFormExpectedDays('')
    setFormWhse('')
    setFormFeLeadDays('')
    onFormOpen()
  }

  const openEdit = (part: TicketPart) => {
    setEditingPart(part)
    setFormPartName(part.part_name)
    setFormPartCode(part.part_code ?? '')
    setFormMrCreatedAt(part.mr_created_at ? part.mr_created_at.slice(0, 16) : '')
    setFormExpectedDays(part.expected_delivery_lead_time_days != null ? String(part.expected_delivery_lead_time_days) : '')
    setFormWhse(part.whse_availability ?? '')
    setFormFeLeadDays(part.fe_lead_time_days != null ? String(part.fe_lead_time_days) : '')
    onFormOpen()
  }

  const handleSubmit = async () => {
    if (!ticketId || !formPartName.trim()) {
      showError(new Error('Part name is required'))
      return
    }
    setSubmitting(true)
    const payload = {
      part_name: formPartName.trim(),
      part_code: formPartCode.trim() || null,
      mr_created_at: formMrCreatedAt ? new Date(formMrCreatedAt).toISOString() : null,
      expected_delivery_lead_time_days: formExpectedDays !== '' ? parseInt(formExpectedDays, 10) : null,
      whse_availability: formWhse || null,
      fe_lead_time_days: formFeLeadDays !== '' ? parseInt(formFeLeadDays, 10) : null,
    }
    const res = editingPart
      ? await TicketService.updateTicketPart(ticketId, editingPart.id, payload)
      : await TicketService.createTicketPart(ticketId, payload)
    setSubmitting(false)
    if (res.success) {
      showSuccess(editingPart ? 'Part updated' : 'Part added')
      onFormClose()
      fetchParts()
      onPartsChange?.()
    } else {
      showError(new Error(res.message ?? 'Failed to save'))
    }
  }

  const handleDelete = async (part: TicketPart) => {
    if (!ticketId || !window.confirm('Remove this part?')) return
    const res = await TicketService.deleteTicketPart(ticketId, part.id)
    if (res.success) {
      showSuccess('Part removed')
      onFormClose()
      setEditingPart(null)
      fetchParts()
      onPartsChange?.()
    } else {
      showError(new Error(res.message ?? 'Failed to remove'))
    }
  }

  if (!ticketId) return null

  return (
    <Box>
      <HStack justify="space-between" align="center" mb={3}>
        <Text fontSize="xs" fontWeight="700" color={colors.textSecondary} textTransform="uppercase" letterSpacing="wider">
          Parts information
        </Text>
        {!isReadOnly && (
          <Button leftIcon={<Plus size={14} />} size="sm" colorScheme="blue" onClick={openAdd}>
            Add parts
          </Button>
        )}
      </HStack>
      <VStack align="stretch" spacing={2}>
        {loading ? (
          <Text fontSize="sm" color={colors.textSecondary}>
            Loading...
          </Text>
        ) : parts.length === 0 ? (
          <Text fontSize="sm" color={colors.textSecondary}>
            No parts recorded. {!isReadOnly && 'Click "Add part" to add one.'}
          </Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Part name</Th>
                <Th>Part code</Th>
                <Th>MR created</Th>
                <Th>Expected delivery (days)</Th>
                <Th>WHSE availability</Th>
                <Th>FE lead time (days)</Th>
                {!isReadOnly && <Th>Actions</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {parts.map((p) => (
                <Tr key={p.id}>
                  <Td>{p.part_name}</Td>
                  <Td>{p.part_code ?? '—'}</Td>
                  <Td>{formatDate(p.mr_created_at ?? undefined)}</Td>
                  <Td>{p.expected_delivery_lead_time_days ?? '—'}</Td>
                  <Td>{p.whse_availability ? (p.whse_availability === 'available' ? 'Available' : 'Not Available') : '—'}</Td>
                  <Td>{p.fe_lead_time_days ?? '—'}</Td>
                  {!isReadOnly && (
                    <Td>
                      <HStack spacing={1}>
                        <IconButton
                          aria-label="Edit part"
                          size="sm"
                          variant="ghost"
                          icon={<Pencil size={14} />}
                          onClick={() => openEdit(p)}
                        />
                        <IconButton
                          aria-label="Delete part"
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          icon={<Trash2 size={14} />}
                          onClick={() => handleDelete(p)}
                        />
                      </HStack>
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </VStack>

      <Modal isOpen={isFormOpen} onClose={() => { onFormClose(); setEditingPart(null) }} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingPart ? 'Edit part' : 'Add part'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel>Part name</FormLabel>
                <Input
                  value={formPartName}
                  onChange={(e) => setFormPartName(e.target.value)}
                  placeholder="Part name"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Part code</FormLabel>
                <Input
                  value={formPartCode}
                  onChange={(e) => setFormPartCode(e.target.value)}
                  placeholder="Part code"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Date & time MR created</FormLabel>
                <Input
                  type="datetime-local"
                  value={formMrCreatedAt}
                  onChange={(e) => setFormMrCreatedAt(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Expected delivery lead time (days)</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={formExpectedDays}
                  onChange={(e) => setFormExpectedDays(e.target.value)}
                  placeholder="Days"
                />
              </FormControl>
              <FormControl>
                <FormLabel>WHSE availability</FormLabel>
                <Select value={formWhse} onChange={(e) => setFormWhse(e.target.value as typeof formWhse)}>
                  <option value="">—</option>
                  {WHSE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Lead time to be received by FE (days)</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={formFeLeadDays}
                  onChange={(e) => setFormFeLeadDays(e.target.value)}
                  placeholder="Days"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => { onFormClose(); setEditingPart(null) }}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={submitting}>
              {editingPart ? 'Save' : 'Add'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
